import { useFormStatus } from "@dippi/forms";
import { Product } from "@/types/entities";
import { Params } from "@/types/utils";
import { classificationCodeToName } from "@/utils/classification/names";
import { getRoutes, shorten } from "@/utils/helpers";
import { useParams } from "react-router-dom";
import useQueryOrganization from "@/hooks/queries/useQueryOrganization";
import useQueryBrands from "@/hooks/queries/useQueryBrands";
import useQueryProducts from "@/hooks/queries/useQueryProducts";

interface BreadcrumbLink {
    route: string;
    text: string;
}

const useBreadcrumbs = (pages: Record<string, boolean>) => {
    const routes = getRoutes();

    const params = useParams<Params>();
    const { bid, pid, typeName } = params;

    const { data: organization } = useQueryOrganization();

    const { data: brands } = useQueryBrands();
    const brand = brands?.find(i => i._id === bid);

    const { data: products } = useQueryProducts();
    const product = products?.find(i => i._id === pid);

    const isDirty = useFormStatus(state => state.isDirty);
    const setModal = useFormStatus(state => state.setModal);

    const onNavLinkClickHandler = (e: React.MouseEvent<HTMLAnchorElement>, route: string) => {
        const isBrandsPage = /^\/organization\/\w+\/brands$/.test(route);
        const isProductsPage = /^\/organization\/\w+\/brands\/\w+\/products$/.test(route);

        if (isDirty) e.preventDefault();

        if (isBrandsPage && isDirty) {
            return setModal(true, routes.brands());
        }

        if (isProductsPage && isDirty) {
            return setModal(true, routes.products());
        }
    };

    const breadcrumbsMap: Record<string, BreadcrumbLink[]> = (() => {
        const organizationLink = {
            text: organization ? organization.title : "Organization",
            route: routes.brands(),
        };

        return {
            organizations: [{ ...organizationLink, route: routes.organizations }],
            brands: [{ ...organizationLink, route: routes.brands() }],
            products: [
                organizationLink,
                { text: brand ? shorten(brand.title, 35) : "Brand", route: routes.products(bid) },
            ],
            brandSettings: bid
                ? [
                      organizationLink,
                      {
                          text: brand ? shorten(brand.title, 35) : "Brand",
                          route: routes.products(bid),
                      },
                      { text: "Brand Settings", route: routes.brandSettings() },
                  ]
                : [organizationLink, { text: "Brand Settings", route: routes.brandSettings() }],
            profile: [organizationLink, { text: "Profile", route: routes.profile }],
            members: [organizationLink, { text: "Members", route: routes.organizationMembers() }],
            settings: [
                organizationLink,
                { text: "Settings", route: routes.organizationSettings() },
            ],
            support: [organizationLink, { text: "Support", route: routes.support }],
            productSettings: [
                organizationLink,
                {
                    text: brand ? shorten(brand.title, 35) : "Brand",
                    route: routes.products(bid),
                },
                {
                    text: `${
                        product
                            ? shorten(product.longTitle, 35)
                            : classificationCodeToName[typeName!]?.title
                    }`,
                    route: routes.productSettings(typeName as Product["typeName"], pid),
                },
            ],
        };
    })();

    let breadcrumbs: BreadcrumbLink[] = [];

    for (const [page, isActive] of Object.entries<boolean>(pages)) {
        const selectedPage = breadcrumbsMap[page];

        if (isActive && selectedPage) {
            breadcrumbs = selectedPage;
            break;
        }
    }

    return { breadcrumbs, onNavLinkClickHandler };
};

export { useBreadcrumbs };
